import { useAuth0 } from '@auth0/auth0-react';
import { DotsThree } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { Button, Divider, Tooltip } from 'src/components';
import { CopyButton } from 'src/components/CopyButton';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DropdownMenu } from 'src/components/DropdownMenu';
import type { DeviceType } from 'src/utils/deviceHelpers';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { formatDate } from 'src/utils/formatDate';
import { useCopyToClipboard } from 'src/utils/useCopyToClipboard';
import type { GeneratedTestUser } from '../utils/generatedUsersStore';
import { useGeneratedTestUsers } from '../utils/generatedUsersStore';
import { roles } from './UserPropertiesForm';

function GeneratedUser({ user }: { user: GeneratedTestUser }) {
  const auth = useAuth0();
  const generatedTestUserStore = useGeneratedTestUsers();
  const copyToClipboard = useCopyToClipboard();

  const handleLogin = async (userName: string) => {
    await auth.loginWithRedirect({
      appState: { returnTo: '/' },
      authorizationParams: {
        prompt: 'login',
        login_hint: userName,
      },
    });
  };

  return (
    <div className="@container flex flex-col gap-8">
      <div className="flex w-full items-center justify-between">
        <Typography variant="interface-lg" className="w-full truncate">
          {user.username}
        </Typography>
        <DropdownMenu
          trigger={
            <Button
              variant="tertiary-neutral"
              size="medium"
              className="!rounded"
            >
              <DotsThree />
            </Button>
          }
          items={[
            {
              label: 'Remove',
              id: 'remove',
              onClick: () => generatedTestUserStore.remove(user),
            },
            user.organization
              ? {
                  label: 'Copy Org ID',
                  id: 'copy-org-id',
                  onClick: () => {
                    void copyToClipboard.mutateAsync(user.organization?.id);
                  },
                }
              : undefined,
          ]}
        ></DropdownMenu>
      </div>

      <div className="flex flex-col justify-between gap-8 @xl:flex-row">
        <div className="flex items-center gap-8">
          {user.createdAt && (
            <Tooltip title={formatDate(user.createdAt)}>
              <Typography variant="interface-sm" className="cursor-default">
                Created:{' '}
                {formatDistanceToNow(new Date(user.createdAt), {
                  addSuffix: true,
                })}
              </Typography>
            </Tooltip>
          )}
          {user.subscription && (
            <Tag variant="info-light">Connect | {user.subscription}</Tag>
          )}
          {user.organization && (
            <Tag variant="success-light">
              {roles.find((r) => r.value === user.organization?.role)?.label ||
                'No role'}
            </Tag>
          )}
          {user.devices.map((d, i) => (
            <Tooltip
              title={deviceHelpers.getDeviceNameFromDeviceType(
                d.toUpperCase() as DeviceType
              )}
              key={i}
            >
              <div>
                <DeviceIcon
                  device={undefined}
                  deviceType={d.toUpperCase() as DeviceType}
                />
              </div>
            </Tooltip>
          ))}
        </div>
        <div className="flex justify-end gap-8 overflow-hidden">
          <CopyButton
            value={user.username}
            size="medium"
            variant="tertiary-neutral"
          >
            Email
          </CopyButton>
          {user.password && (
            <CopyButton
              value={user.password}
              size="medium"
              variant="tertiary-neutral"
            >
              Password
            </CopyButton>
          )}
          <Button
            variant="primary"
            size="medium"
            onClick={() => handleLogin(user.username)}
          >
            Login
          </Button>
        </div>
      </div>
    </div>
  );
}

export function GeneratedUsers() {
  const generatedTestUsers = useGeneratedTestUsers();

  return (
    <div className="flex w-full flex-col gap-24 overflow-scroll border border-paper-8 p-24">
      <div className="flex items-center justify-between gap-16">
        <Typography as="h2" variant="heading-lg">
          Generated users{' '}
          {generatedTestUsers.data.length
            ? `(${generatedTestUsers.data.length})`
            : ''}
        </Typography>

        <DropdownMenu
          trigger={
            <Button
              variant="tertiary-neutral"
              size="medium"
              className="!rounded"
            >
              <DotsThree />
            </Button>
          }
          items={[
            {
              label: 'Remove all',
              id: 'remove-all',
              onClick: () => generatedTestUsers.clear(),
            },
          ]}
        ></DropdownMenu>
      </div>
      {generatedTestUsers.data.length === 0 ? (
        <Typography variant="body-lg">No generated users yet.</Typography>
      ) : (
        <div className="flex flex-col gap-8">
          {generatedTestUsers.data.toReversed().map((user) => (
            <React.Fragment key={user.username}>
              <Divider />
              <GeneratedUser key={user.username} user={user} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
